import { nil, NO, UIAutocompleteTextField, UITextField, UIView, YES } from "uicore-ts"
import { CBDataViewCellDescriptor } from "../CB/CBDataView"
import { CBTableHeaderView } from "../CB/CBTableHeaderView"
import { RHAutocompleteTextField } from "./RHAutocompleteTextField"
import { RHDatePicker } from "./RHDatePicker"
import { RHDropdown } from "./RHDropdown"
import { RHTextField } from "./RHTextField"
import { RHTooltip } from "./RHTooltip"
import { RHTables } from "./Utils_slop/RHDesignTokens"

/**
 * RHTableHeaderView
 * Styles: Bootstrap <thead>
 */
export class RHTableHeaderView<DataType extends object> extends CBTableHeaderView<DataType> {

    backgroundView = new UIView().configuredWithObject({
        backgroundColor: RHTables.headerBackgroundColor,
        style: {
            borderBottom: `${RHTables.headerBorderBottomWidth} solid ${RHTables.headerBorderBottomColor.stringValue}`
        }
    }).addedAsSubviewToView(this)

    /**
     * When YES the header background and bottom border span the full header width
     * instead of being inset to the content area (sideCellsWidth on each side).
     * Opt-in — the default NO keeps every existing table's inset header unchanged.
     * The CPV picker dialog enables it so its single-column heading reads as a
     * full-width banner from edge to edge of the table.
     */
    backgroundSpansFullWidth = NO

    constructor(elementID: string, descriptors: CBDataViewCellDescriptor<DataType>[] = []) {

        super(elementID, descriptors);

        (this as RHTableHeaderView<DataType>).configureWithObject({
            // backgroundColor: RHTableHeaderTokens.backgroundColor,
            style: {
                // // Change to 1px to match data rows
                // borderBottom: `1px solid ${BSColor.borderLight.stringValue}`,
                // // Prevent padding/border from adding to the width
                // boxSizing: "border-box"
            }
        })

    }


    protected override _newFilterTextField(
        elementID: string,
        placeholderText: string,
        inputType?: string,
        descriptor?: CBDataViewCellDescriptor<DataType>
    ): UIView {
        const filterDescriptor = descriptor?.filter
        if (filterDescriptor?.control === "autocomplete") {
            const autocompleteTextField = new RHAutocompleteTextField<string>(elementID).configuredWithObject({
                placeholderText,
                usesMultiWordAndSearch: filterDescriptor.usesMultiWordAndSearch ?? YES,
                strictSelection: filterDescriptor.strictSelection ?? NO,
                validatesAgainstAutocomplete: filterDescriptor.validatesAgainstAutocomplete ??
                    filterDescriptor.strictSelection ??
                    NO,
                style: {
                    width: "100%",
                    height: "100%",
                    minHeight: "0",
                    fontSize: "12px",
                    padding: "4px 6px",
                    boxSizing: "border-box"
                },
                textElementView: {
                    style: {
                        fontSize: "12px"
                    }
                }
            })

            const autocompleteStrings = filterDescriptor.autocompleteStrings
            if (Array.isArray(autocompleteStrings)) {
                autocompleteTextField.autocompleteStrings = autocompleteStrings
            }
            else if (autocompleteStrings) {
                Promise.resolve(autocompleteStrings()).then(strings => {
                    autocompleteTextField.autocompleteStrings = strings
                }).catch(error => {
                    console.warn("RHTableHeaderView: failed to load autocomplete filter options", error)
                })
            }

            autocompleteTextField.addTargetForControlEvent(
                UIAutocompleteTextField.controlEvent.SelectionDidChange,
                () => {
                    autocompleteTextField.sendControlEventForKey(UITextField.controlEvent.TextChange, nil)
                }
            )

            return autocompleteTextField
        }

        const textField = new RHTextField(elementID).configuredWithObject({
            placeholderText,
            style: {
                width: "100%",
                height: "100%",
                minHeight: "0",
                fontSize: "12px",
                padding: "4px 6px",
                boxSizing: "border-box"
            },
            textElementView: {
                style: {
                    fontSize: "12px"
                }
            }
        })
        if (inputType === "number") {
            textField.type = "number"
            textField.allowDecimals = true
        }
        return textField
    }


    protected override _newFilterNumberField(elementID: string, placeholderText: string): UIView {
        return this._newFilterTextField(elementID, placeholderText, "number")
    }


    protected override _newFilterDateField(elementID: string, placeholderText: string): UIView {
        return new RHDatePicker(elementID).configuredWithObject({
            type: "date",
            placeholder: placeholderText,
            style: {
                width: "100%",
                height: "100%",
                minHeight: "0",
                padding: "4px 6px",
                boxSizing: "border-box"
            },
            titleLabel: {
                style: {
                    fontSize: "12px"
                }
            }
        })
    }


    protected override _newFilterCategoryDropdown(
        elementID: string,
        placeholderText: string,
        options: { value: string; label: string }[]
    ): UIView {
        const dropdown = new RHDropdown<string>(elementID).configuredWithObject({
            options: [
                { label: placeholderText, value: "" },
                ...options.map(option => ({
                    label: option.label,
                    value: option.value
                }))
            ],
            style: {
                width: "100%",
                height: "100%",
                minHeight: "0",
                fontSize: "12px"
            }
        })
        dropdown.labelText = ""

        const selectElement = dropdown.viewHTMLElement.querySelector("select") as HTMLSelectElement | null
        if (selectElement) {
            Object.assign(selectElement.style, {
                fontSize: "12px",
                padding: "4px 24px 4px 6px",
                borderRadius: "4px",
                boxSizing: "border-box"
            })
        }

        return dropdown
    }


    protected override _addFilterControlChangeHandler(control: UIView, handler: () => void) {
        if (control instanceof RHDatePicker) {
            control.addTargetForControlEvent(RHDatePicker.controlEvent.DateChange, handler)
            return
        }
        super._addFilterControlChangeHandler(control, handler)
    }


    protected override _addFilterCategoryDropdownChangeHandler(control: UIView, handler: () => void) {
        if (control instanceof RHDropdown) {
            control.addTargetForControlEvent(RHDropdown.controlEvent.SelectionDidChange, handler)
            return
        }
        super._addFilterCategoryDropdownChangeHandler(control, handler)
    }


    protected override _filterControlText(control: UIView | undefined): string {
        if (control instanceof RHDatePicker) {
            const selectedDate = control.selectedDate
            return selectedDate ? this._dateFilterStringFromDate(selectedDate) : ""
        }
        return super._filterControlText(control)
    }


    protected override _clearFilterControl(control: UIView | undefined) {
        if (control instanceof RHDatePicker) {
            control.clear()
            return
        }
        super._clearFilterControl(control)
    }


    protected override _selectedValuesFromCategoryDropdown(selectView: UIView | undefined): string[] {
        if (selectView instanceof RHDropdown) {
            const value = selectView.selectedValue
            return value === undefined || value.length === 0 ? [] : [value]
        }
        return super._selectedValuesFromCategoryDropdown(selectView)
    }


    protected override _clearFilterCategoryDropdown(control: UIView | undefined) {
        if (control instanceof RHDropdown) {
            control.reset()
            return
        }
        super._clearFilterCategoryDropdown(control)
    }


    private _dateFilterStringFromDate(date: Date): string {
        const year = date.getFullYear()
        const month = `${date.getMonth() + 1}`.padStart(2, "0")
        const day = `${date.getDate()}`.padStart(2, "0")
        return `${year}-${month}-${day}`
    }


    override updateContentForButtonStates() {
        super.updateContentForButtonStates()

        // `this.cells` holds only the VISIBLE column headers, while `this.descriptors`
        // still includes hidden ones. Map each visible cell back to its real descriptor
        // via `_visibleDescriptorIndices` (maintained by CBTableHeaderView). Indexing
        // `this.descriptors[index]` directly mis-assigns per-column settings whenever a
        // table hides a descriptor — e.g. a centred date header inheriting a hidden
        // column's left alignment, which left-shifts the header off its centred data.
        this.cells.forEach((cell, index) => {
            const fullIndex = this._visibleDescriptorIndices[index] ?? index
            const descriptor = this.descriptors[fullIndex]
            const titleLabelAlignment = descriptor?.titleLabelAlignment || RHTables.headerTextAlignment
            const rightImageView = cell._rightImageView

            if (rightImageView) {
                rightImageView.configureWithObject({
                    style: { opacity: RHTables.headerSortIconOpacity }
                })
            }

            cell.titleLabel?.configureWithObject({
                textAlignment: titleLabelAlignment,
                style: {
                    fontWeight: RHTables.headerFontWeight,
                    fontSize: RHTables.headerFontSize,
                    fontFamily: RHTables.headerFontFamily,
                    letterSpacing: RHTables.headerLetterSpacing,
                    textTransform: "none",
                    verticalAlign: "bottom"
                }
            })

            // Two-line header titles (e.g. "Vastab hanke-<br>tingimustele") would
            // otherwise surface their raw markup in the native hover tooltip:
            // UIButton falls back to the raw label text for its title on every
            // layout pass. Pre-empt it with the plain text.
            if (cell.buttonView) {
                cell.buttonView.hoverText = RHTooltip.plainTextFromMarkup(cell.buttonView.titleLabel.text)
            }

            cell.configureWithObject({

                buttonView: {
                    titleLabel: {
                        style: {
                            color: RHTables.headerTextColor.stringValue,
                            fontWeight: RHTables.headerFontWeight,
                            fontSize: RHTables.headerFontSize,
                            fontFamily: RHTables.headerFontFamily,
                            letterSpacing: RHTables.headerLetterSpacing,
                            textTransform: "none",
                            verticalAlign: "bottom"
                        }
                    },
                    style: {
                        padding: RHTables.headerCellPadding

                    },
                    colors: {
                        titleLabel: {
                            normal: RHTables.headerTextColor,
                            highlighted: RHTables.headerActiveTextColor,
                            selected: RHTables.headerActiveTextColor
                        },
                        background: {
                            normal: RHTables.headerBackgroundColor,
                            hovered: RHTables.headerBackgroundColor,
                            highlighted: RHTables.headerBackgroundColor,
                            selected: RHTables.headerBackgroundColor
                        }
                    }
                }
            })

        })
    }

    override layoutSubviews() {
        super.layoutSubviews()
        if (this.backgroundSpansFullWidth) {
            this.backgroundView.weakFrame = this.bounds
        }
        else {
            this.backgroundView.weakFrame = this.contentBounds
        }
    }

    override intrinsicContentHeight(constrainingWidth?: number): number {
        return super.intrinsicContentHeight(constrainingWidth)
    }


}
